import { useWindowSize } from "@uidotdev/usehooks";
import clsx from "clsx";
import { ErrorBoundary } from "react-error-boundary";

import { Image, type ImageProps } from "@/components/atoms/media/Image";
import { Source, type SourceProps } from "@/components/atoms/media/Source";
import { IMAGES_METRICS, TRANSPARENT } from "@/consts";
import { ImageProviderEnum, ImageVariantEnum } from "@/enums";
import { type CommonProps } from "@/types";
import {
  cloudinarySource,
  contentfulSource,
  datasetFlag,
  containerCSSVars,
  createId,
  setCSSVariables,
} from "@/utils/helpers";

import styles from "./Picture.module.css";

const ref = "picture";

interface PictureProps
  extends Partial<CommonProps>,
    Partial<Pick<HTMLElement, "title">> {
  active?: boolean;
  image: ImageProps;
  index?: number;
}

function defaultZoom(variant: ImageVariantEnum) {
  switch (variant) {
    case ImageVariantEnum.CARD:
    case ImageVariantEnum.GRID:
    case ImageVariantEnum.PRIMARY:
    case ImageVariantEnum.SEARCH:
    case ImageVariantEnum.SECONDARY:
      return true;

    default:
      return false;
  }
}

function setProvider(image: ImageProps) {
  const { src } = image;
  let { provider } = image;

  if (!provider) {
    if (src.includes("cloudinary")) {
      provider = ImageProviderEnum.CLOUDINARY;
    } else if (src.includes("ctfassets")) {
      provider = ImageProviderEnum.CONTENTFUL;
    } else {
      provider = ImageProviderEnum.BUCKET;
    }
  }

  return provider;
}

function sources(image: ImageProps) {
  const imageMetrics = IMAGES_METRICS[image.variant || ImageVariantEnum.NONE];

  const urls = Object.entries(imageMetrics)
    .reverse()
    .flatMap(([mediaWidth, metrics]) => {
      return image.provider === ImageProviderEnum.CLOUDINARY
        ? cloudinarySource(image.src, +mediaWidth, metrics)
        : contentfulSource(image.src, +mediaWidth, metrics);
    });

  urls.pop();

  return urls;
}

function usePicture(props: PictureProps) {
  const image = {
    title: props.title,
    provider: setProvider(props.image),
    ...props.image,
  };
  const { variant = ImageVariantEnum.NONE } = image;
  const className = clsx(
    styles[ref],
    defaultZoom(variant) && "zoom",
    props.className,
  );
  const { id = createId(ref), index } = props;
  const sourcesArray: Array<SourceProps> = [];
  const vars = containerCSSVars(useWindowSize(), image);

  if (image.src && image.provider !== ImageProviderEnum.BUCKET) {
    sourcesArray.push(...sources(image));
  }

  return {
    active: !!props.active,
    className,
    dataType: variant !== ImageVariantEnum.NONE ? variant : null,
    id,
    image,
    index,
    sources: sourcesArray,
    vars: setCSSVariables(vars),
  };
}

function Picture(props: PictureProps) {
  const { active, className, dataType, id, image, index, sources, vars } =
    usePicture(props);

  if (!props.image.src) {
    return null;
  }

  return (
    <picture
      className={className}
      id={id}
      style={vars}
      data-active={datasetFlag(active)}
      data-index={index ?? null}
      data-type={dataType}
    >
      <ErrorBoundary
        fallbackRender={() => (
          <img alt={image.alt ?? ""} src={TRANSPARENT} data-status={"error"} />
        )}
      >
        {sources.map((source, index) => (
          <Source key={`${id}_source_${index}`} {...source} />
        ))}
        <Image {...image} />
      </ErrorBoundary>
    </picture>
  );
}

export default Picture;
export { Picture, type PictureProps };
